import { styled } from "@material-ui/styles";
import { Linked, useIO, useLinked, useModel } from "@type-r/react";
import { Button, Column, Columns, Flex, Link, Loading, PrimaryButton, div, navigate, span } from "lqx-react";
import React, { useEffect, useMemo } from "react";
import { AssetEventType } from "../api/assetEvent";
import { ErrorView, ErrorsContainer } from "../shared";
import { isJSON } from "../utilities/jsonExtensions";
import { AssetState } from "./components/assets-table";
import { Can } from "src/app/services/can";
import { UserEntitlementCatalogs } from "src/app/models/UserEntitlements";

export const EditAssetForm = (
    { assetChange, assetModel, $doAssetUpdated, onAssetDeleted, assetForm, actions }:
        { assetChange: any, assetModel: any, $doAssetUpdated: Linked<boolean>, onAssetDeleted?, actions?: JSX.Element, assetForm: (asset) => JSX.Element }) => {

    const asset = useModel(assetModel);
    const $isAssetProcessedByDownstreamProduct = useLinked<boolean>(false);

    useEffect(() => {
        asset.transaction(() => {
            assetChange.syncTo(asset);
            assetChange.updateWithInfoFromApi(asset);
            $doAssetUpdated.set(true);
        });

        $isAssetProcessedByDownstreamProduct.set(() => {
            return assetChange.state.name == AssetState.Digitized || (assetChange.assetEvents.length &&
                assetChange.assetEvents.models.some(ae => (ae.eventTypeId == AssetEventType.inblock_mapper || ae.eventTypeId == AssetEventType.scf_ingestor) && ae.success));
        }
        );
    }, [assetChange['_changeToken']]);

    const errors = useMemo(() => {
        return Object.entries(assetChange.errors)
            .map(([key, value]) => key.length >= 0 ? value : null)
            .filter(v => v != null);
    }, [assetChange['_changeToken']]);

    const $doUpdateAssetIO = useLinked(false);
    const isUpdateAssetReady = useIO(
        async () => $doUpdateAssetIO.isTruthy && await updateSelectedAsset(),
        [$doUpdateAssetIO.value]
    );

    const $doDeleteAssetIO = useLinked(false);
    const isDeleteAssetReady = useIO(
        async () => $doDeleteAssetIO.isTruthy && await deleteSelectedAsset(),
        [$doDeleteAssetIO.value]
    );

    const updateSelectedAsset = async () => {
        try {
            assetChange.syncFrom(asset);
            return await assetChange.save();
        } finally {
            $doUpdateAssetIO.set(false);
        }
    }

    const deleteSelectedAsset = async () => {
        try {
            const result = await assetChange.destroy();
            onAssetDeleted();
            return result;
        } finally {
            $doDeleteAssetIO.set(false);
        }
    }

    return isUpdateAssetReady && isDeleteAssetReady ?
        <>
            {errors.length > 0 &&
                <ErrorsContainer>
                    {errors.map((error: any, index) => {
                        if (isJSON(error)) {
                            const erroJson = JSON.parse(error);
                            const importJobIds = isJSON(erroJson.ImportJobIds) ? JSON.parse(erroJson.ImportJobIds) : [];
                            return <ErrorView key={index}>{erroJson.Message}
                                {importJobIds.length ?
                                    <ImportJobDetails>: Import job(s){
                                        importJobIds.map((importJobId, index) => {
                                            return <span>{index === 0 ? ' ' : ', '}<Link href="" onClick={() => navigate(`/ingest/edit/${importJobId}`)}>{importJobId}</Link></span>
                                        })
                                    }
                                    </ImportJobDetails> :
                                    <></>}
                            </ErrorView>
                        } else {
                            return <ErrorView key={index}>{error}</ErrorView>
                        }
                    })}
                </ErrorsContainer>}
            <EditAssetFormContainer>
                <Flex center>
                    <Title>{assetChange.getAssetName()} Details: {asset.number}</Title>
                    {actions}
                </Flex>
                <ContentContainer {...{ active: Boolean($isAssetProcessedByDownstreamProduct.value).toString() }}>
                    {assetForm(asset)}
                </ContentContainer>
                <BottomTools center>
                    <Columns>
                        <Column>
                            <ButtonContainer>
                                {
                                    Can(UserEntitlementCatalogs.DeleteJobAssets) &&
                                    <Button disabled={Boolean($isAssetProcessedByDownstreamProduct.value)} qa-id={"delete" + assetChange.getAssetType()} color="primary" onClick={$doDeleteAssetIO.true}>Delete {assetChange.getAssetName()}</Button>
                                }
                            </ButtonContainer>
                        </Column>
                        <Column>
                            <ButtonContainer>
                                {
                                    Can(UserEntitlementCatalogs.UpdateJobAssets) &&
                                    <PrimaryButton disabled={Boolean($isAssetProcessedByDownstreamProduct.value)} qa-id={"update" + assetChange.getAssetType()} onClick={$doUpdateAssetIO.true}>Update {assetChange.getAssetName()}</PrimaryButton>
                                }
                            </ButtonContainer>
                        </Column>
                    </Columns>
                </BottomTools>
            </EditAssetFormContainer>
        </> : <Loading />
}

export const ButtonContainer = div({
    marginBottom: '5px',
    paddingLeft: '15px',
    paddingRight: '15px',
    alignSelf: 'center'
});

export const EditAssetFormContainer = div({
    background: '#FFFFFF',
    border: '1px solid #bdc3c7',
    borderBottomLeftRadius: '4px',
    borderBottomRightRadius: '4px',
    padding: '0px',
    paddingTop: '10px',
    height: '100%',
    overflow: 'auto'
});

export const ContentContainer = div({
    pointerEvents: (props: any) => props.active === 'true' ? 'none' : 'auto',
    opacity: (props: any) => props.active === 'true' ? '0.7' : '1'
});

export const Title = div({
    padding: 12,
    alignSelf: 'center',
    width: '100%',
    fontSize: 'larger',
    fontWeight: 500,
    color: '#5f839e',
    textAlign: 'center'
});

const BottomTools = styled(Flex)({
    position: 'sticky',
    bottom: 0,
    //zIndex: 1,
    background: 'white',
    padding: '10px 0 10px 0'
})

export const ImportJobDetails = span({
    color: 'grey'
})